import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, MoreHorizontal, FileSpreadsheet, FileText, Ban,
} from 'lucide-react'
import { FeaturedLayout } from '../../layouts'
import {
  Button, Badge, Card, CardContent, Input, Label, Textarea, Checkbox,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '../../ui'
import {
  getPedido, actualizarPedido, marcarPedidoListo, regresarPedidoABorrador,
  marcarPedidoEnviado, cancelarPedido, recibirLinea, recibirItems, quitarDePedido,
  getTemporadaById, actualizarImpresoDePrenda,
} from '../../../lib/supabase'
import {
  estadoPedido, PEDIDO_ESTADO_LABEL, PEDIDO_ESTADO_BADGE, numeroEfectivo, nicknameEfectivo,
} from '../../../lib/uniformesReglas'
// jspdf + jspdf-autotable no tienen otro punto de entrada en el bundle (a diferencia de
// xlsx-populate, que ya cargan ListaEntregas/pagosExport): importar este modulo estatico
// metia ~450 KB sin comprimir al chunk principal. Se carga bajo demanda, solo al descargar.
import { useConfirmDialog } from '../../../hooks/useConfirmDialog'
import { useOrganization } from '../../../contexts/OrganizationContext'
import { formatDate } from '../../../utils/helpers'
import { estadoDePago } from '../../../lib/uniformesEstadoPago'
import { useDeportes } from '../../../hooks/useDeportes'

const todayISO = () => new Date().toISOString().slice(0, 10)

// Igual que en CompartirRolModal: el logo puede vivir en otro dominio (Storage),
// asi que hay que traerlo como data URL para que jsPDF pueda incrustarlo.
const urlToDataUrl = async (url) => {
  try {
    const res = await fetch(url, { mode: 'cors' })
    const blob = await res.blob()
    return await new Promise((resolve) => {
      const fr = new FileReader()
      fr.onload = () => resolve(fr.result)
      fr.onerror = () => resolve(null)
      fr.readAsDataURL(blob)
    })
  } catch { return null }
}

const PedidoDetallePage = () => {
  const { label: deporteLabel } = useDeportes()
  const { id } = useParams()
  const navigate = useNavigate()
  const { confirm, alert, ConfirmDialog } = useConfirmDialog()
  const { orgName, orgLogo } = useOrganization()

  const [pedido, setPedido] = useState(null)
  const [lineas, setLineas] = useState([])
  const [items, setItems] = useState([])
  const [temporadaNombre, setTemporadaNombre] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const { data, error } = await getPedido(id)
    if (error || !data) {
      setPedido(null); setLineas([]); setItems([])
      setNotFound(true)
      setLoading(false)
      return
    }
    setNotFound(false)
    setPedido(data.pedido)
    setLineas(data.lineas || [])
    setItems(data.items || [])
    setLoading(false)

    if (data.pedido?.temporada_id) {
      const { data: temp } = await getTemporadaById(data.pedido.temporada_id)
      setTemporadaNombre(temp?.nombre || null)
    } else {
      setTemporadaNombre(null)
    }
  }, [id])

  useEffect(() => { load() }, [load])

  // ---- Cabecera (editable solo en borrador) ----
  const [form, setForm] = useState({ proveedor: '', fecha_pedido: '', fecha_pago: '', notas: '' })
  useEffect(() => {
    if (!pedido) return
    setForm({
      proveedor: pedido.proveedor || '',
      fecha_pedido: (pedido.fecha_pedido || '').slice(0, 10),
      fecha_pago: (pedido.fecha_pago || '').slice(0, 10),
      notas: pedido.notas || '',
    })
  }, [pedido])

  const isBorrador = pedido?.estado === 'borrador'
  const isListo = pedido?.estado === 'listo'
  const isEnviado = pedido?.estado === 'enviado'
  const isCancelado = pedido?.estado === 'cancelado'

  const cabeceraDirty = !!pedido && (
    form.proveedor !== (pedido.proveedor || '') ||
    form.fecha_pedido !== (pedido.fecha_pedido || '').slice(0, 10) ||
    form.fecha_pago !== (pedido.fecha_pago || '').slice(0, 10) ||
    form.notas !== (pedido.notas || '')
  )

  const handleGuardarCabecera = async () => {
    if (!form.proveedor.trim()) { await alert('Falta el proveedor', 'El proveedor es obligatorio'); return }
    setSaving(true)
    const { error } = await actualizarPedido(id, {
      proveedor: form.proveedor,
      fecha_pedido: form.fecha_pedido || null,
      fecha_pago: form.fecha_pago || null,
      notas: form.notas || null,
    })
    setSaving(false)
    if (error) { await alert('Error', error.message || 'No se pudo guardar el pedido'); return }
    await load()
  }

  // ---- Datos derivados ----
  const prendaById = useMemo(() => {
    const map = {}
    lineas.forEach((l) => { if (l.prenda_id && l.prendas) map[l.prenda_id] = l.prendas })
    items.forEach((it) => { if (it.prenda_id && !map[it.prenda_id] && it.prendas) map[it.prenda_id] = it.prendas })
    return map
  }, [lineas, items])

  const jugadorById = useMemo(() => {
    const map = {}
    items.forEach((it) => { if (it.jugador_id && it.jugadores) map[it.jugador_id] = it.jugadores })
    return map
  }, [items])

  const itemsByLinea = useMemo(() => {
    const map = {}
    items.forEach((it) => {
      const key = it.linea_id || 'sin_linea'
      ;(map[key] = map[key] || []).push(it)
    })
    return map
  }, [items])

  const estado = pedido ? estadoPedido(pedido, lineas) : 'borrador'

  // ---- Ciclo de vida ----
  const handleMarcarListo = async () => {
    setSaving(true)
    const { error } = await marcarPedidoListo(id)
    setSaving(false)
    if (error) { await alert('Error', error.message || 'No se pudo marcar el pedido como listo'); return }
    await alert('Pedido listo', 'El pedido quedó listo para enviarse al proveedor.')
    await load()
  }

  const [enviarDialog, setEnviarDialog] = useState({ open: false, fecha: todayISO() })
  const openEnviarDialog = () => setEnviarDialog({ open: true, fecha: todayISO() })
  const handleConfirmEnviar = async () => {
    setSaving(true)
    const { error } = await marcarPedidoEnviado(id, enviarDialog.fecha)
    setSaving(false)
    if (error) { await alert('Error', error.message || 'No se pudo marcar el pedido como enviado'); return }
    setEnviarDialog({ open: false, fecha: todayISO() })
    await alert('Pedido enviado', `El pedido a "${pedido.proveedor}" se marcó como enviado.`)
    await load()
  }

  const handleRegresarABorrador = async () => {
    setSaving(true)
    const { error } = await regresarPedidoABorrador(id)
    setSaving(false)
    if (error) { await alert('Error', error.message || 'No se pudo regresar el pedido a borrador'); return }
    await alert('Pedido en borrador', 'El pedido regresó a borrador y puede volver a editarse.')
    await load()
  }

  const handleCancelar = async () => {
    const recibidas = lineas.reduce((s, l) => s + (l.cantidad_recibida || 0), 0)
    const ok = await confirm(
      'Cancelar pedido',
      recibidas > 0
        ? `Este pedido tiene ${recibidas} prenda(s) ya recibidas; cancelarlo las regresa a "Por pedir". El pedido se conserva en el historial.`
        : `¿Cancelar el pedido a "${pedido.proveedor}"? Sus prendas regresan a "Por pedir" y el pedido se conserva en el historial.`,
      { destructive: true },
    )
    if (!ok) return
    setSaving(true)
    const { error } = await cancelarPedido(id)
    setSaving(false)
    if (error) { await alert('Error', error.message || 'No se pudo cancelar el pedido'); return }
    await alert('Pedido cancelado', `El pedido a "${pedido.proveedor}" quedó cancelado y se conserva en el historial.`)
    await load()
  }

  // ---- Descargas ----
  const handleExportExcel = async () => {
    try {
      const { exportarPedidoExcel } = await import('./documentosPedido')
      await exportarPedidoExcel({ pedido, lineas, items }, { prendaById, jugadorById, orgNombre: orgName })
    } catch (e) {
      await alert('Error', e.message || 'No se pudo generar el Excel')
    }
  }

  const handleExportPDF = async () => {
    try {
      const [{ exportarPedidoPDF }, logoDataUrl] = await Promise.all([
        import('./documentosPedido'),
        orgLogo ? urlToDataUrl(orgLogo) : Promise.resolve(null),
      ])
      exportarPedidoPDF({ pedido, lineas }, { prendaById, orgNombre: orgName, logoDataUrl })
    } catch (e) {
      await alert('Error', e.message || 'No se pudo generar el PDF')
    }
  }

  // ---- Recepción por línea genérica ----
  const [recepcionInputs, setRecepcionInputs] = useState({})
  useEffect(() => {
    const next = {}
    lineas.forEach((l) => { next[l.id] = String(l.cantidad_recibida ?? 0) })
    setRecepcionInputs(next)
  }, [lineas])

  const [savingLineaId, setSavingLineaId] = useState(null)
  const handleRecibirLinea = async (linea) => {
    const n = Number(recepcionInputs[linea.id])
    if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
      await alert('Cantidad inválida', 'Ingresa un número entero mayor o igual a cero')
      return
    }
    setSavingLineaId(linea.id)
    const { error } = await recibirLinea(linea.id, n)
    setSavingLineaId(null)
    if (error) { await alert('Error', error.message || 'No se pudo registrar la recepción'); return }
    await load()
  }

  // ---- Recepción por línea personalizada (por jugador) ----
  const [itemsDialog, setItemsDialog] = useState({ open: false, linea: null, checks: new Set() })
  const [savingItems, setSavingItems] = useState(false)

  const openItemsDialog = (linea) => {
    const its = itemsByLinea[linea.id] || []
    setItemsDialog({ open: true, linea, checks: new Set(its.filter((it) => it.recibido_at).map((it) => it.id)) })
  }
  const closeItemsDialog = () => setItemsDialog({ open: false, linea: null, checks: new Set() })
  const toggleItemCheck = (itemId) => {
    setItemsDialog((prev) => {
      const next = new Set(prev.checks)
      if (next.has(itemId)) next.delete(itemId); else next.add(itemId)
      return { ...prev, checks: next }
    })
  }

  const handleGuardarRecepcionItems = async () => {
    if (!itemsDialog.linea) return
    const its = itemsByLinea[itemsDialog.linea.id] || []
    const originally = new Set(its.filter((it) => it.recibido_at).map((it) => it.id))
    const nowChecked = itemsDialog.checks
    const toReceive = [...nowChecked].filter((iid) => !originally.has(iid))
    const toUnreceive = [...originally].filter((iid) => !nowChecked.has(iid))

    setSavingItems(true)
    if (toReceive.length) {
      const { error } = await recibirItems(toReceive, true)
      if (error) { setSavingItems(false); await alert('Error', error.message || 'No se pudo marcar la recepción'); return }
    }
    if (toUnreceive.length) {
      const { error } = await recibirItems(toUnreceive, false)
      if (error) { setSavingItems(false); await alert('Error', error.message || 'No se pudo actualizar la recepción'); return }
    }
    setSavingItems(false)
    closeItemsDialog()
    await load()
  }

  // ---- Quitar del pedido (solo borrador) ----
  // Lo que va bordado. Se edita en un dialogo y no en la fila: son dos campos que
  // casi nunca se tocan, y meterlos como inputs en cada renglon no cabe en un telefono.
  const [impresoDialog, setImpresoDialog] = useState({ open: false, item: null })
  const [impresoForm, setImpresoForm] = useState({ numero: '', nickname: '' })
  const [impresoSaving, setImpresoSaving] = useState(false)

  const openImpreso = (item) => {
    const jugador = jugadorById[item.jugador_id] || item.jugadores || {}
    const n = numeroEfectivo(item, jugador)
    const nk = nicknameEfectivo(item, jugador)
    setImpresoForm({ numero: n != null ? String(n) : '', nickname: nk || '' })
    setImpresoDialog({ open: true, item })
  }

  const handleGuardarImpreso = async () => {
    if (!impresoDialog.item) return
    setImpresoSaving(true)
    const { error } = await actualizarImpresoDePrenda(impresoDialog.item.id, {
      numero_impreso: impresoForm.numero,
      nickname_impreso: impresoForm.nickname,
    })
    setImpresoSaving(false)
    if (error) { await alert('Error', error.message || 'No se pudo guardar'); return }
    setImpresoDialog({ open: false, item: null })
    await load()
  }

  const handleQuitarItem = async (item) => {
    const jugador = jugadorById[item.jugador_id]
    const prenda = prendaById[item.prenda_id]
    const ok = await confirm(
      'Quitar del pedido',
      `¿Quitar ${prenda?.nombre || 'la prenda'} talla ${item.talla || ''} de ${jugador?.nombre_completo || 'este jugador'}? Vuelve a "Por pedir".`,
      { destructive: true },
    )
    if (!ok) return
    const { error } = await quitarDePedido([item.id])
    if (error) { await alert('Error', error.message || 'No se pudo quitar la prenda del pedido'); return }
    await load()
  }

  // El titulo es el PROVEEDOR: es lo que distingue un pedido de otro. "Pedido" a
  // secas obligaba a leer el subtitulo para saber cual estabas viendo, y en la
  // pestana del navegador todos se llamaban igual.
  const titulo = pedido?.proveedor || 'Pedido'
  const subtitle = pedido
    ? `Pedido del ${formatDate(pedido.fecha_pedido)}`
    : 'Detalle del pedido'

  return (
    <FeaturedLayout title={titulo} subtitle={subtitle} refreshAction={load}>
      <div className="mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/uniformes/pedidos')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Pedidos
        </Button>
      </div>

      {loading ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Cargando...</p>
      ) : notFound || !pedido ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <p>No se encontró el pedido.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Cabecera */}
          <Card className="mb-4">
            <CardContent className="pt-6 space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-1">
                  <Badge variant={PEDIDO_ESTADO_BADGE[estado]}>{PEDIDO_ESTADO_LABEL[estado] || estado}</Badge>
                  {isEnviado && pedido.enviado_at && (
                    <p className="text-xs text-muted-foreground">Enviado el {formatDate(pedido.enviado_at)}</p>
                  )}
                  {isCancelado && pedido.cancelado_at && (
                    <p className="text-xs text-muted-foreground">Cancelado el {formatDate(pedido.cancelado_at)}</p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {isBorrador && (
                    <Button
                      onClick={handleMarcarListo}
                      disabled={saving || lineas.length === 0}
                      title={lineas.length === 0 ? 'Agrega al menos una prenda antes de marcarlo listo' : undefined}
                    >
                      {saving ? 'Guardando...' : 'Marcar listo'}
                    </Button>
                  )}
                  {isListo && (
                    <>
                      <Button onClick={openEnviarDialog} disabled={saving}>Marcar enviado</Button>
                      <Button variant="outline" onClick={handleRegresarABorrador} disabled={saving}>
                        {saving ? 'Guardando...' : 'Regresar a borrador'}
                      </Button>
                    </>
                  )}

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon" aria-label="Más acciones" title="Más acciones">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={handleExportExcel}>
                        <FileSpreadsheet className="h-4 w-4" /> Descargar Excel
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleExportPDF}>
                        <FileText className="h-4 w-4" /> Descargar PDF
                      </DropdownMenuItem>
                      {!isCancelado && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={handleCancelar} className="text-destructive focus:text-destructive">
                            <Ban className="h-4 w-4" /> Cancelar pedido
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-1.5">
                  <Label>Proveedor {isBorrador && '*'}</Label>
                  {isBorrador ? (
                    <Input value={form.proveedor} onChange={(e) => setForm((p) => ({ ...p, proveedor: e.target.value }))} />
                  ) : (
                    <p className="text-sm font-medium">{pedido.proveedor || '—'}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Fecha del pedido</Label>
                  {isBorrador ? (
                    <Input type="date" value={form.fecha_pedido} onChange={(e) => setForm((p) => ({ ...p, fecha_pedido: e.target.value }))} />
                  ) : (
                    <p className="text-sm">{formatDate(pedido.fecha_pedido)}</p>
                  )}
                </div>
                {/* La fecha de pago es la que se les cobra a las familias: cada pieza
                    del pedido toma esta fecha, y si no tenia cobro se le genera. */}
                <div className="space-y-1.5">
                  <Label>Fecha de pago</Label>
                  {isBorrador ? (
                    <Input type="date" value={form.fecha_pago} onChange={(e) => setForm((p) => ({ ...p, fecha_pago: e.target.value }))} />
                  ) : (
                    <p className="text-sm">
                      {pedido.fecha_pago
                        ? formatDate(pedido.fecha_pago)
                        : <span className="text-muted-foreground">Sin fecha de pago</span>}
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Temporada</Label>
                  <p className="text-sm">{temporadaNombre || '—'}</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Deporte</Label>
                  <p className="text-sm">{deporteLabel(pedido.deporte || 'todos')}</p>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Notas {isBorrador && <span className="text-muted-foreground font-normal">(opcional)</span>}</Label>
                {isBorrador ? (
                  <Textarea
                    value={form.notas}
                    onChange={(e) => setForm((p) => ({ ...p, notas: e.target.value }))}
                    placeholder="Notas para el proveedor..."
                    rows={3}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{pedido.notas || '—'}</p>
                )}
              </div>

              {isBorrador && cabeceraDirty && (
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setForm({
                    proveedor: pedido.proveedor || '',
                    fecha_pedido: (pedido.fecha_pedido || '').slice(0, 10),
                    fecha_pago: (pedido.fecha_pago || '').slice(0, 10),
                    notas: pedido.notas || '',
                  })} disabled={saving}>
                    Deshacer
                  </Button>
                  <Button onClick={handleGuardarCabecera} disabled={saving}>
                    {saving ? 'Guardando...' : 'Guardar cambios'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sin fecha de pago el pedido se puede armar y mandar, pero su dinero no
              existe en ningun lado. Se avisa, no se bloquea: el club decide. */}
          {isBorrador && !pedido.fecha_pago && (
            <p className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
              Este pedido no tiene fecha de pago. Las prendas que lleva no van a generar
              cobro ni aparecer en el estado de cuenta hasta que se la pongas.
            </p>
          )}

          {/* Líneas */}
          <Card className="mb-4">
            <CardContent className="pt-6">
              <h3 className="mb-3 text-sm font-semibold">Líneas del pedido</h3>
              {lineas.length === 0 ? (
                /* Ningun vacio sin salida: un borrador recien creado nace asi, y
                   desde aqui no se agregan prendas -- se agregan desde "Por pedir",
                   que es donde estan las que ya tienen talla. */
                <div className="py-6 text-center">
                  <p className="text-sm text-muted-foreground">Este pedido no tiene prendas todavía.</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Agrégalas desde "Por pedir": selecciona las que ya tienen talla y
                    elige este pedido en "Agregar a...".
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => navigate('/admin/uniformes/pedidos')}
                  >
                    Ir a Por pedir
                  </Button>
                </div>
              ) : (
                <>
                  {/* Móvil */}
                  <div className="sm:hidden divide-y">
                    {lineas.map((linea) => {
                      const prenda = prendaById[linea.prenda_id]
                      const personalizada = !!prenda?.personalizada
                      return (
                        <div key={linea.id} className="py-3 space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium text-sm">{prenda?.nombre || 'Prenda'}</span>
                            <Badge variant="outline">{linea.talla}</Badge>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Pedidas <span className="font-medium text-foreground">{linea.cantidad_pedida}</span>
                            {' · '}
                            Recibidas <span className="font-medium text-foreground">{linea.cantidad_recibida}</span>
                          </div>
                          {isEnviado && (
                            personalizada ? (
                              <Button variant="outline" size="sm" onClick={() => openItemsDialog(linea)}>
                                Marcar recibidas
                              </Button>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number" min="0" max={linea.cantidad_pedida}
                                  className="h-9 w-24"
                                  value={recepcionInputs[linea.id] ?? ''}
                                  onChange={(e) => setRecepcionInputs((p) => ({ ...p, [linea.id]: e.target.value }))}
                                />
                                <Button
                                  size="sm"
                                  onClick={() => handleRecibirLinea(linea)}
                                  disabled={savingLineaId === linea.id}
                                >
                                  {savingLineaId === linea.id ? 'Guardando...' : 'Recibir'}
                                </Button>
                              </div>
                            )
                          )}
                        </div>
                      )
                    })}
                  </div>

                  {/* Escritorio */}
                  <div className="hidden sm:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Prenda</TableHead>
                          <TableHead>Talla</TableHead>
                          <TableHead>Pedidas</TableHead>
                          <TableHead>Recibidas</TableHead>
                          {isEnviado && <TableHead>Recepción</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {lineas.map((linea) => {
                          const prenda = prendaById[linea.prenda_id]
                          const personalizada = !!prenda?.personalizada
                          return (
                            <TableRow key={linea.id}>
                              <TableCell className="font-medium">{prenda?.nombre || 'Prenda'}</TableCell>
                              <TableCell><Badge variant="outline">{linea.talla}</Badge></TableCell>
                              <TableCell>{linea.cantidad_pedida}</TableCell>
                              <TableCell>{linea.cantidad_recibida}</TableCell>
                              {isEnviado && (
                                <TableCell>
                                  {personalizada ? (
                                    <Button variant="outline" size="sm" onClick={() => openItemsDialog(linea)}>
                                      Marcar recibidas
                                    </Button>
                                  ) : (
                                    <div className="flex items-center gap-2">
                                      <Input
                                        type="number" min="0" max={linea.cantidad_pedida}
                                        className="h-8 w-20"
                                        value={recepcionInputs[linea.id] ?? ''}
                                        onChange={(e) => setRecepcionInputs((p) => ({ ...p, [linea.id]: e.target.value }))}
                                      />
                                      <Button
                                        size="sm"
                                        onClick={() => handleRecibirLinea(linea)}
                                        disabled={savingLineaId === linea.id}
                                      >
                                        {savingLineaId === linea.id ? 'Guardando...' : 'Recibir'}
                                      </Button>
                                    </div>
                                  )}
                                </TableCell>
                              )}
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Desglose por jugador */}
          <Card>
            <CardContent className="pt-6">
              <h3 className="mb-3 text-sm font-semibold">Desglose por jugador</h3>
              {items.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">Este pedido no tiene jugadores asignados.</p>
              ) : (
                <>
                  {/* Móvil: nombres son texto libre, siempre lista */}
                  <div className="sm:hidden divide-y">
                    {items.map((item) => {
                      const jugador = jugadorById[item.jugador_id] || item.jugadores || {}
                      const prenda = prendaById[item.prenda_id] || item.prendas || {}
                      const personalizada = !!prenda.personalizada
                      const numero = personalizada ? numeroEfectivo(item, jugador) : null
                      const nickname = personalizada ? nicknameEfectivo(item, jugador) : null
                      return (
                        <div key={item.id} className="py-3 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">
                              {jugador.nombre_completo || '—'}
                              {numero != null && <span className="text-muted-foreground"> · #{numero}</span>}
                              {nickname && <span className="text-muted-foreground"> · {nickname}</span>}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {prenda.nombre || 'Prenda'} · {item.talla}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Badge variant={estadoDePago(item).variant} className="text-[10px]">
                              {estadoDePago(item).label}
                            </Badge>
                            {isBorrador && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Más acciones">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {personalizada && (
                                    <DropdownMenuItem onClick={() => openImpreso(item)}>
                                      Editar numero y nickname
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem
                                    onClick={() => handleQuitarItem(item)}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    Quitar del pedido
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {/* Escritorio */}
                  <div className="hidden sm:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Jugador</TableHead>
                          <TableHead>Número</TableHead>
                          <TableHead>Nickname</TableHead>
                          <TableHead>Prenda</TableHead>
                          <TableHead>Talla</TableHead>
                          <TableHead>Pago</TableHead>
                          {isBorrador && <TableHead className="w-10"></TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((item) => {
                          const jugador = jugadorById[item.jugador_id] || item.jugadores || {}
                          const prenda = prendaById[item.prenda_id] || item.prendas || {}
                          const personalizada = !!prenda.personalizada
                          const numero = personalizada ? numeroEfectivo(item, jugador) : null
                          const nickname = personalizada ? nicknameEfectivo(item, jugador) : null
                          return (
                            <TableRow key={item.id}>
                              <TableCell className="font-medium">{jugador.nombre_completo || '—'}</TableCell>
                              <TableCell>{numero != null ? `#${numero}` : '—'}</TableCell>
                              <TableCell>{nickname || '—'}</TableCell>
                              <TableCell>{prenda.nombre || 'Prenda'}</TableCell>
                              <TableCell><Badge variant="outline">{item.talla}</Badge></TableCell>
                              <TableCell>
                                <Badge variant={estadoDePago(item).variant}>
                                  {estadoDePago(item).label}
                                </Badge>
                              </TableCell>
                              {isBorrador && (
                                <TableCell>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Más acciones">
                                        <MoreHorizontal className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        {personalizada && (
                                          <DropdownMenuItem onClick={() => openImpreso(item)}>
                                            Editar numero y nickname
                                          </DropdownMenuItem>
                                        )}
                                      <DropdownMenuItem
                                        onClick={() => handleQuitarItem(item)}
                                        className="text-destructive focus:text-destructive"
                                      >
                                        Quitar del pedido
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              )}
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Dialog: marcar enviado */}
      <Dialog open={enviarDialog.open} onOpenChange={(open) => { if (!open) setEnviarDialog({ open: false, fecha: todayISO() }) }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Marcar como enviado</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Fecha de envío</Label>
            <Input
              type="date"
              value={enviarDialog.fecha}
              onChange={(e) => setEnviarDialog((p) => ({ ...p, fecha: e.target.value }))}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnviarDialog({ open: false, fecha: todayISO() })} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleConfirmEnviar} disabled={saving || !enviarDialog.fecha}>
              {saving ? 'Guardando...' : 'Marcar enviado'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: marcar recibidas (prenda personalizada) */}
      <Dialog open={itemsDialog.open} onOpenChange={(open) => { if (!open) closeItemsDialog() }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Marcar recibidas — {prendaById[itemsDialog.linea?.prenda_id]?.nombre} talla {itemsDialog.linea?.talla}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto divide-y">
            {(itemsByLinea[itemsDialog.linea?.id] || []).map((item) => {
              const jugador = jugadorById[item.jugador_id] || item.jugadores || {}
              const numero = numeroEfectivo(item, jugador)
              const nickname = nicknameEfectivo(item, jugador)
              return (
                <label key={item.id} className="flex items-center gap-3 py-2 cursor-pointer">
                  <Checkbox
                    checked={itemsDialog.checks.has(item.id)}
                    onCheckedChange={() => toggleItemCheck(item.id)}
                  />
                  <span className="text-sm">
                    {jugador.nombre_completo || '—'}
                    {numero != null && <span className="text-muted-foreground"> · #{numero}</span>}
                    {nickname && <span className="text-muted-foreground"> · {nickname}</span>}
                  </span>
                </label>
              )
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeItemsDialog} disabled={savingItems}>Cerrar</Button>
            <Button onClick={handleGuardarRecepcionItems} disabled={savingItems}>
              {savingItems ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={impresoDialog.open} onOpenChange={(open) => { if (!open) setImpresoDialog({ open: false, item: null }) }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              Número y nickname — {jugadorById[impresoDialog.item?.jugador_id]?.nombre_completo || 'Jugador'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Esto es lo que va bordado en esta prenda. Al marcar el pedido como listo queda
              congelado: cambiar el número del jugador en su grupo ya no lo modifica.
            </p>
            <div className="space-y-1">
              <Label htmlFor="numero-impreso">Número</Label>
              <Input
                id="numero-impreso"
                value={impresoForm.numero}
                onChange={(e) => setImpresoForm((p) => ({ ...p, numero: e.target.value }))}
                placeholder="Sin número"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="nickname-impreso">Nickname</Label>
              <Input
                id="nickname-impreso"
                value={impresoForm.nickname}
                onChange={(e) => setImpresoForm((p) => ({ ...p, nickname: e.target.value }))}
                placeholder="Sin nickname"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImpresoDialog({ open: false, item: null })} disabled={impresoSaving}>
              Cancelar
            </Button>
            <Button onClick={handleGuardarImpreso} disabled={impresoSaving}>
              {impresoSaving ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </FeaturedLayout>
  )
}

export default PedidoDetallePage
